/**
 * bridge/toast_controller.js — "toast" bridge component (Stimulus + Strada).
 *
 * Capability-tier bridge component: shows a transient floating message that
 * auto-dismisses (e.g. "Quote sent"). Stimulus identifier "bridge--toast"
 * (glob-derived from this subdir); Strada component name "toast" MUST match the
 * iOS class var name (native/ios/Chippie/Bridge/ToastComponent.swift) and the
 * Android-reached method name.
 *
 * Why it extends Strada's BridgeComponent: the iOS native overlay is reached via
 * this.send("show", ...), which only works on a connected bridge component inside
 * the iOS Hotwire Native shell. Extending BridgeComponent gives us that send()
 * transport AND the UA-gated connect() (it only connects in a native shell).
 *
 * Three-way transport via the window.ChippieBridge facade. Each call resolves to
 * the right impl for the current platform; callers just do
 * window.ChippieBridge.toast("Quote sent").
 *   - ios:     this.send("show", { message }) over Strada to ToastComponent.swift
 *   - android: window.ChippieNative.toast(message) (@JavascriptInterface)
 *   - pwa:     reuse the app's existing toast UI by dispatching the `toasts:show`
 *              event that toasts_controller.js already listens for. No new DOM.
 *
 * Standalone registration: the iOS impl needs a live bridge instance (this.send),
 * which only exists while this controller is connected (inside the iOS shell). On
 * Android and PWA this controller never connects, so we register the
 * Android/PWA-capable triple at module load too (a safe ios no-op placeholder),
 * making window.ChippieBridge.toast() work even with no element on the page. The
 * facade is the SSOT for which impl runs per platform.
 *
 * Template wiring (optional):
 *   <button data-controller="bridge--toast"
 *           data-bridge--toast-message-value="Quote sent"
 *           data-action="bridge--toast#show">Send</button>
 * Or call window.ChippieBridge.toast("Quote sent") directly from any JS.
 *
 * Web side adapted from joemasilotti/bridge-components toast_controller.js.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// PWA fallback: render through the app's own transient toast UI rather than
// building bespoke DOM. toasts_controller.js (on a permanent #toast-root) listens
// for `toasts:show`. Plain message -> info-style toast.
function pwaToast(message) {
    if (!message) return;
    document.dispatchEvent(
        new CustomEvent("toasts:show", { detail: { message: String(message), type: "info" } })
    );
}

// Android: the native @JavascriptInterface Toast. Guarded so a missing/older
// bridge degrades to the PWA toast instead of throwing.
function androidToast(message) {
    if (window.ChippieNative && window.ChippieNative.toast) {
        try { window.ChippieNative.toast(String(message || "")); return; } catch (_e) { /* fall through */ }
    }
    pwaToast(message);
}

// Android + PWA impls are platform-static (no per-instance state), so register
// them once at module load. ios is a no-op placeholder until connect() supplies a
// real `this.send`. The facade picks the right one per call.
function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("toast", {
        ios: () => {}, // overridden by connect() with a live Strada send
        android: androidToast,
        pwa: pwaToast,
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "toast";

    static values = {
        message: String,
    };

    connect() {
        super.connect();
        // Inside the iOS shell now: wire the real Strada send into the facade,
        // keeping the Android/PWA fallbacks intact.
        if (!window.ChippieBridge || !window.ChippieBridge.register) return;
        window.ChippieBridge.register("toast", {
            ios: (message) => this.send("show", { message: String(message || "") }, () => {}),
            android: androidToast,
            pwa: pwaToast,
        });
    }

    disconnect() {
        // Leaving the iOS shell context: drop the live-send impl back to the
        // standalone placeholder so a stale `this.send` is never invoked.
        registerStandalone();
        super.disconnect();
    }

    // data-action="bridge--toast#show" trigger — shows this element's message.
    show(event) {
        if (event) {
            event.preventDefault();
            if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        }
        window.ChippieBridge.toast(this.messageValue);
    }
}
